import React from "react";
import { Heart, Activity, Brain, Shield, Repeat2, Waves } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";
import { productUrl } from "@/config/siteUrls";

const PHYSIOLOGICAL = [
  {
    icon: Heart,
    title: "Widened HRV",
    body:
      "Increased autonomic capacity — a larger range of stress the system can absorb before defaulting.",
    testId: "outcome-hrv",
  },
  {
    icon: Waves,
    title: "Improved Vagal Access",
    body:
      "Faster return to a regulated state after activation. The parasympathetic branch becomes more readily available.",
    testId: "outcome-vagal",
  },
  {
    icon: Activity,
    title: "Recalibrated Stress Response",
    body:
      "A normalized diurnal cortisol curve — not blunted, but appropriately shaped to the day.",
    testId: "outcome-cortisol",
  },
  {
    icon: Brain,
    title: "Enhanced Neuroplasticity Substrate",
    body:
      "Upregulated BDNF expression through consistent exercise input, supporting the encoding of new patterns.",
    testId: "outcome-bdnf",
  },
];

const BEHAVIORAL = [
  {
    title: "Impulse latency increases",
    body: "The gap between trigger and response widens — creating room for choice.",
  },
  {
    title: "Recurring patterns lose grip",
    body:
      "Behaviors that used to fire automatically require deliberate effort — a signal the default has shifted.",
  },
  {
    title: "Self-regulation becomes ambient",
    body:
      "Routines that once demanded willpower begin to run themselves.",
  },
  {
    title: "Engagement returns",
    body:
      "Meaningful activity, connection, and recreation become accessible again.",
  },
  {
    title: "Recovery accelerates",
    body:
      "The system returns to baseline faster after emotional, physical, or social load.",
  },
  {
    title: "Identity begins to update",
    body:
      "The self-story adjusts to match the new default — the last durable outcome, and the most important one.",
  },
];

const HORIZONS = [
  {
    window: "First 4 weeks",
    body:
      "Early physiological signal changes — sleep quality, HRV, resting heart rate, subjective energy. Behavioral changes are initiated but not yet stable.",
  },
  {
    window: "Weeks 4–12",
    body:
      "Set-point begins to shift. Behavioral patterns that previously required willpower start to run with less friction. Prescription adapts to the new baseline.",
  },
  {
    window: "Months 3–12",
    body:
      "The methodology moves from intervention into maintenance. The pillar of Adaptive Performance takes over — the individual carries forward the loop.",
  },
];

const Outcomes = () => {
  return (
    <PageShell>
      <SubHero
        testId="outcomes-hero"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Methodology", to: "/methodology" },
          { label: "Outcomes" },
        ]}
        overline="Outcomes"
        headline="What Adaptive Physiology"
        headlineAccent="measurably shifts."
        lead="Outcomes are reported at two layers: physiological — the markers we track — and behavioral — the lived, observable changes those markers make possible. Adaptive Physiology™ makes no absolute claims. It reports what shifts, and how it is measured."
      />

      <section
        data-testid="outcomes-physiological-section"
        className="relative py-24 sm:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="01"
              label="Physiological Outcomes"
              testId="outcomes-physiological-overline"
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              The variables we track and expect to move.
            </h2>
          </Reveal>

          <div className="mt-16 grid sm:grid-cols-2 gap-6">
            {PHYSIOLOGICAL.map((p, i) => (
              <Reveal delay={i * 0.06} key={p.title}>
                <div
                  data-testid={`card-${p.testId}`}
                  className="forr-card rounded-[24px] p-8 h-full"
                >
                  <div className="w-11 h-11 rounded-2xl bg-teal-tint border border-teal-soft flex items-center justify-center forr-text-teal-deep mb-6">
                    <p.icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display forr-text-ink text-xl font-medium tracking-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm font-light forr-text-soft leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="outcomes-behavioral-section"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <SectionOverline
                  number="02"
                  label="Behavioral Outcomes"
                  testId="outcomes-behavioral-overline"
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  Signs the physiology is retraining.
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8">
              <ul className="space-y-4">
                {BEHAVIORAL.map((b, i) => (
                  <Reveal delay={i * 0.04} key={b.title}>
                    <li
                      data-testid={`outcome-behavioral-${i + 1}`}
                      className="flex items-start gap-6 border-b border pb-5"
                    >
                      <div className="font-display forr-text-teal-deep text-sm font-medium tracking-tight mt-1 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <div className="font-display forr-text-ink text-lg font-medium tracking-tight">
                          {b.title}
                        </div>
                        <div className="mt-1 text-sm font-light forr-text-soft leading-relaxed">
                          {b.body}
                        </div>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section
        data-testid="outcomes-timeline-section"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="03"
              label="Realistic Time Horizons"
              testId="outcomes-timeline-overline"
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              The nervous system adapts on its own clock.
            </h2>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {HORIZONS.map((h, i) => (
              <Reveal delay={i * 0.06} key={h.window}>
                <div
                  data-testid={`outcome-horizon-${i + 1}`}
                  className="forr-card rounded-[24px] p-8 h-full"
                >
                  <div className="text-[11px] uppercase tracking-[0.28em] forr-text-teal-deep mb-4">
                    {h.window}
                  </div>
                  <p className="text-sm font-light forr-text-soft leading-relaxed">
                    {h.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="outcomes-honesty-section"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="max-w-3xl">
              <div className="w-11 h-11 rounded-2xl bg-teal-tint border border-teal-soft flex items-center justify-center forr-text-teal-deep mb-6">
                <Shield size={20} strokeWidth={1.5} />
              </div>
              <h2 className="font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                We do not guarantee outcomes. We measure them.
              </h2>
              <p className="mt-8 text-lg font-light forr-text-soft leading-relaxed">
                Adaptive Physiology™ is a methodology, not a promise. Outcomes
                depend on the state of the system before the loop begins, on the
                consistency of signal delivery, and on individual physiology.
              </p>
              <p className="mt-6 text-lg font-light forr-text-soft leading-relaxed">
                What we commit to is transparency: the markers we track are
                shared, the protocol we prescribe is explained, and the changes
                we observe are reported honestly — direction, magnitude, and
                limits included.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <NextStep
        overline="Explore next"
        title="Take the Adaptive Assessment"
        description="The Adaptive Assessment is available now and provides a personalized read of your current nervous-system pattern inside the framework."
        to={productUrl("/assessment/start")}
        cta="Take the Assessment"
        testId="outcomes-next-step"
      />
    </PageShell>
  );
};

export default Outcomes;
